
import { Project, EVMSnapshot, ComplianceRisk, BudgetItem, AiReport, ProjectDocument, UserSettings, ActionItem, ProposalDraft, ChangeOrder, ComplianceRequirement, Subcontractor, ProjectLog, AuditLogEntry, KnowledgeBaseDocument, Phase, Activity, Dependency, Calendar, BaseEntity } from '../types';

// Support both number (legacy) and string (UUID) IDs
type ID = string | number;

interface IStorageProvider<T> {
  list(filter?: (item: T) => boolean): Promise<T[]>;
  get(id: ID): Promise<T | undefined>;
  add(item: T): Promise<ID>;
  update(id: ID, updates: Partial<T>): Promise<void>;
  delete(id: ID): Promise<void>;
  clear(): Promise<void>;
  subscribe(listener: () => void): () => void;
  getSnapshot(): number;
}

/**
 * Cloudflare D1 Provider (Remote SQL)
 * For production workloads requiring relational integrity and edge performance.
 */
class CloudflareD1Provider<T extends { id?: ID, createdAt?: string, createdBy?: string, syncStatus?: string }> implements IStorageProvider<T> {
  private endpoint: string;
  private storeName: string;
  private version = 0;
  private listeners: Set<() => void> = new Set();

  constructor(storeName: string) {
    this.storeName = storeName;
    const baseUrl = import.meta.env.VITE_BACKEND_URL || '';
    this.endpoint = `${baseUrl}/api/${this.mapStoreToTable(storeName)}`;
  }

  private mapStoreToTable(store: string): string {
    const map: Record<string, string> = {
      'projects': 'projects',
      'evm': 'evm',
      'changeOrders': 'change_orders',
      'projectLogs': 'project_logs',
      'wbsTasks': 'wbs_tasks', // Legacy
      'subcontractors': 'subcontractors',
      'auditLogs': 'audit_logs',
      'settings': 'settings',
      'knowledgeBase': 'knowledge_base',
      'phases': 'phases',
      'activities': 'activities',
      'dependencies': 'dependencies',
      'calendars': 'calendars'
    };
    return map[store] || store;
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot() { return this.version; }

  private notify() {
    this.version++;
    this.listeners.forEach(l => l());
  }

  async list(filter?: (item: T) => boolean): Promise<T[]> {
    try {
      const res = await fetch(this.endpoint);
      const results = await res.json();
      const parsed = results.map((r: any) => this.parseItem(r));
      return filter ? parsed.filter(filter) : parsed;
    } catch (e) {
      console.error(`D1 List Error [${this.storeName}]:`, e);
      return [];
    }
  }

  async get(id: ID): Promise<T | undefined> {
    try {
      const res = await fetch(`${this.endpoint}/${id}`);
      const result = await res.json();
      return this.parseItem(result);
    } catch (e) { return undefined; }
  }

  async add(item: T): Promise<ID> {
    try {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });
      const { id } = await res.json();
      this.notify();
      return id;
    } catch (e) { return Date.now(); }
  }

  async update(id: ID, updates: Partial<T>): Promise<void> {
    try {
      await fetch(`${this.endpoint}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      this.notify();
    } catch (e) { }
  }

  async delete(id: ID): Promise<void> {
    try {
      await fetch(`${this.endpoint}/${id}`, { method: 'DELETE' });
      this.notify();
    } catch (e) { }
  }

  async clear(): Promise<void> {
    this.notify();
  }

  private parseItem(item: any): T {
    const result = { ...item };
    // Parse known JSON fields
    ['tags', 'photos', 'auditResult', 'workingDays', 'holidays'].forEach(key => {
      if (typeof result[key] === 'string') {
        try { result[key] = JSON.parse(result[key]); } catch (e) { }
      }
    });
    return result as T;
  }
}

class IndexedDBProvider<T extends { id?: ID, createdAt?: string, createdBy?: string, syncStatus?: string }> implements IStorageProvider<T> {
  private storeName: string;
  private listeners: Set<() => void> = new Set();
  private storeVersion = 0;
  private static dbPromise: Promise<IDBDatabase> | null = null;

  constructor(storeName: string) {
    this.storeName = `lc_${storeName}`;
  }

  private async getDB(): Promise<IDBDatabase> {
    if (IndexedDBProvider.dbPromise) return IndexedDBProvider.dbPromise;
    IndexedDBProvider.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open('LeanControls_Enterprise_V2', 4); // Bumped version to 4
      request.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        const stores = [
          'projects', 'issues', 'evm', 'risks', 'budget',
          'reports', 'documents', 'settings', 'tasks',
          'checklistItems', 'proposals', 'changeOrders',
          'complianceRequirements', 'subcontractors', 'wbsTasks',
          'projectLogs', 'auditLogs',
          'phases', 'activities', 'dependencies', 'calendars'
        ];
        stores.forEach(s => {
          const name = `lc_${s}`;
          if (!db.objectStoreNames.contains(name)) {
            // For new UUID tables, we might want manual keys, but 'id' keyPath works for both if we provide it.
            // autoIncrement is convenient for numbers foundation.
            db.createObjectStore(name, { keyPath: 'id', autoIncrement: true });
          }
        });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return IndexedDBProvider.dbPromise;
  }

  private async perform(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest): Promise<any> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, mode);
      const store = transaction.objectStore(this.storeName);
      const request = action(store);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.storeVersion++;
    this.listeners.forEach(l => l());
  }

  async list(filter?: (item: T) => boolean): Promise<T[]> {
    try {
      const all = await this.perform('readonly', (s) => s.getAll()) as T[];
      return filter ? all.filter(filter) : all;
    } catch (e) { return []; }
  }

  async get(id: ID): Promise<T | undefined> {
    try { return await this.perform('readonly', (s) => s.get(id)); } catch (e) { return undefined; }
  }

  async add(item: T): Promise<ID> {
    const auditItem = {
      ...item,
      createdAt: item.createdAt || new Date().toISOString(),
      createdBy: item.createdBy || 'CURRENT_OPERATOR',
      syncStatus: 'Pending' as const
    };
    // If ID is missing and it's a UUID table, relying on manual gen or DB.
    // IndexedDB will duplicate if ID is missing and autoIncrement is true.
    const result = await this.perform('readwrite', (s) => s.add(auditItem));
    this.notify();
    return result as ID;
  }

  async update(id: ID, updates: Partial<T>): Promise<void> {
    const existing = await this.get(id);
    if (existing) {
      await this.perform('readwrite', (s) => s.put({
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
        syncStatus: 'Pending' as const
      }));
      this.notify();
    }
  }

  async delete(id: ID): Promise<void> {
    await this.perform('readwrite', (s) => s.delete(id));
    this.notify();
  }

  async clear(): Promise<void> {
    await this.perform('readwrite', (s) => s.clear());
    this.notify();
  }

  async getPending(): Promise<T[]> {
    return this.list(item => item.syncStatus === 'Pending');
  }

  async markSynced(id: ID): Promise<void> {
    const item = await this.get(id);
    if (item) {
      await this.perform('readwrite', (s) => s.put({ ...item, syncStatus: 'Synced' }));
      this.notify();
    }
  }

  getSnapshot() { return this.storeVersion; }
}

const createProvider = <T extends { id?: ID, createdAt?: string, createdBy?: string, syncStatus?: string }>(name: string): IStorageProvider<T> => {
  const useRemote = localStorage.getItem('lc_provider') === 'cloudflare';
  return useRemote ? new CloudflareD1Provider<T>(name) : new IndexedDBProvider<T>(name);
};

export const api = {
  projects: createProvider<Project>('projects'),
  issues: createProvider<ActionItem>('issues'), // Mapped to legacy
  evm: createProvider<EVMSnapshot>('evm'),
  risks: createProvider<ComplianceRisk>('risks'),
  budgetItems: createProvider<BudgetItem>('budget'),
  reports: createProvider<AiReport>('reports'),
  documents: createProvider<ProjectDocument>('documents'),
  settings: createProvider<UserSettings>('settings'),
  tasks: createProvider<ActionItem>('tasks'),
  checklistItems: createProvider<ActionItem>('checklistItems'),
  proposals: createProvider<ProposalDraft>('proposals'),
  changeOrders: createProvider<ChangeOrder>('changeOrders'),
  complianceRequirements: createProvider<ComplianceRequirement>('complianceRequirements'),
  subcontractors: createProvider<Subcontractor>('subcontractors'),
  projectLogs: createProvider<ProjectLog>('projectLogs'),
  auditLogs: createProvider<AuditLogEntry>('auditLogs'),
  knowledgeBase: createProvider<KnowledgeBaseDocument>('knowledgeBase'),

  // P6-Lite
  phases: createProvider<Phase>('phases'),
  activities: createProvider<Activity>('activities'),
  dependencies: createProvider<Dependency>('dependencies'),
  calendars: createProvider<Calendar>('calendars'),

  // Legacy
  // We keep wbsTasks for now for migration, typed as 'any' or legacy interface to avoid strict conflicts if reused
  wbsTasks: createProvider<any>('wbsTasks'),

  logAction: async (projectId: number, action: string, type: string, id: number, details: string) => {
    await api.auditLogs.add({
      projectId,
      action,
      entityType: type,
      entityId: id,
      details,
      createdAt: new Date().toISOString()
    });
  },

  seed: async () => {
    // Seed logic... (simplified/kept same)
    const existingProjects = await api.projects.list();
    if (existingProjects.length > 0) return;

    const pid = await api.projects.add({
      title: "Tumon Luxury Suites Phase II",
      description: "Construction of 40 luxury residential units. Private commercial contract.",
      contractAmount: 14500000,
      startDate: "2024-03-01",
      endDate: "2025-06-30",
      status: "Active",
      client: "Pacific Horizon Dev",
      location: "Tumon Bay, Guam",

      // New fields defaults
      scheduleHealth: "On Track",
      currentFinishDate: "2025-06-30",
      originalContractValue: 14500000,
      approvedCOTotal: 0,
      pendingCOTotal: 0,
      projectedFinalValue: 14500000,
      calendarId: 'default',
      baselineFinishDate: null
    });

    // Legacy seed tasks...
    // In future, seed Phases/Activities here
  }
};
